// Session-scoped `fetch` + `sendBeacon` wrappers enforcing the Guest
// zero-network invariant at runtime.
//
// Two-step install:
//   1. This module runs at import time and CAPTURES the native
//      `globalThis.fetch` into a module-local constant. Nothing on
//      `globalThis` changes yet.
//   2. `main.tsx` calls `installAuthorizedFetch()` after imports
//      resolve, swapping `globalThis.fetch` for `authorizedFetch`
//      AND installing a `navigator.sendBeacon` shim. From that point
//      every `fetch()` and every `sendBeacon()` — from our code and
//      any third-party library reaching for the global (Sonner,
//      React Router, future) — is gated against the allow-list in
//      `./allowList.ts`. Intercepting a named import would leak;
//      the global replacement is the only way to catch everyone.
//
// Guest sessions run with an empty allow-list, so every outbound
// `fetch()` rejects with `NetworkPolicyError` and every
// `sendBeacon()` returns `false` (matches the browser's quota-
// denial contract; existing callers see the same signal they'd get
// from any other beacon refusal). Provider adapters (M3+) extend
// the allow-list at sign-in.

import { isAllowed } from './allowList';

// Query parameters that MUST NOT ride into error messages / logs /
// telemetry. When M3+ providers land, the wrapper may still block a
// URL that already carries a live token in its query string (stale
// endpoint, misconfigured allow-list). Redacting here keeps the
// token out of Sonner toasts, console output, and any downstream
// sink that consumes `.message` or `.blockedUrl`.
const SENSITIVE_QUERY_PARAMS: readonly string[] = [
  'access_token',
  'id_token',
  'refresh_token',
  'code',
  'client_secret',
  'assertion',
  'session_token',
];

function redactSensitiveQuery(rawUrl: string): string {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    // Not a parseable URL — return unchanged; the caller wanted to
    // surface an opaque identifier and any redaction attempt would
    // just mangle it.
    return rawUrl;
  }
  let mutated = false;
  for (const key of SENSITIVE_QUERY_PARAMS) {
    if (parsed.searchParams.has(key)) {
      parsed.searchParams.set(key, '<redacted>');
      mutated = true;
    }
  }
  return mutated ? parsed.toString() : rawUrl;
}

/**
 * Thrown when a `fetch` (or `sendBeacon`) call targets a URL whose
 * origin is not on the session allow-list. The message identifies
 * the blocked URL with sensitive query parameters (`access_token`,
 * `id_token`, `refresh_token`, `code`, `client_secret`, …) replaced
 * by `<redacted>` so a token accidentally in the query string is not
 * echoed into toasts / logs / telemetry.
 *
 * The class extends `Error` — callers unaware of the wrapper see a
 * rejected promise identical to a network failure and can handle it
 * via ordinary `try/catch` or `.catch()`.
 */
export class NetworkPolicyError extends Error {
  public readonly blockedUrl: string;

  public constructor(blockedUrl: string) {
    const safeUrl = redactSensitiveQuery(blockedUrl);
    super(
      `NetworkPolicyError: outbound fetch to "${safeUrl}" was blocked. ` +
        `Origin is not on the session allow-list. See docs/dev/security-shipping.md.`,
    );
    this.name = 'NetworkPolicyError';
    this.blockedUrl = safeUrl;
  }
}

// Sentinel marks the wrapper so `installAuthorizedFetch()` is
// idempotent under Vite HMR / repeat module imports. Without this,
// a second install would treat the wrapper as the "native" fetch and
// double-wrap, which is harmless in production (no HMR) but yields
// confusing stack traces in dev.
const WRAPPER_MARKER: unique symbol = Symbol.for('recto.authorizedFetch.wrapper');
const NATIVE_KEY: unique symbol = Symbol.for('recto.authorizedFetch.native');

type FetchFn = typeof fetch;
type MarkedFetch = FetchFn & { readonly [WRAPPER_MARKER]?: true };
type GlobalWithNative = typeof globalThis & { [NATIVE_KEY]?: FetchFn };

// Capture the pre-install `fetch` exactly once. Under HMR this
// module can re-execute after `installAuthorizedFetch()` already
// swapped `globalThis.fetch` for the wrapper — in that case
// `globalThis.fetch` IS the wrapper and reading it here would give
// us a wrapper reference, breaking the delegation chain. The stash
// on `globalThis` survives the module re-execution and lets us
// recover the true native.
function captureNativeFetch(): FetchFn {
  const g = globalThis as GlobalWithNative;
  const stashed = g[NATIVE_KEY];
  if (stashed !== undefined) {
    return stashed;
  }
  const current = globalThis.fetch as MarkedFetch;
  if (current[WRAPPER_MARKER] === true) {
    // A wrapper is already installed but the native ref was never
    // stashed — should not happen in normal flow; fail loud so the
    // bug is caught in dev instead of a silent recursion.
    throw new Error(
      'authorizedFetch: cannot capture native fetch — wrapper installed without stash.',
    );
  }
  g[NATIVE_KEY] = current;
  return current;
}

const nativeFetch = captureNativeFetch();

/**
 * Fetch wrapper. Identical signature and return type to `fetch`. If
 * the target URL's origin is on the allow-list, delegates to the
 * captured native fetch. Otherwise returns a rejected promise with a
 * `NetworkPolicyError`.
 *
 * The wrapper never mutates its arguments and never inspects the
 * request body — the decision is origin-only. Callers can rely on
 * the delegated fetch receiving the exact `input` and `init` they
 * passed.
 */
export const authorizedFetch: FetchFn = ((input, init) => {
  let target: URL;
  try {
    target = extractUrl(input);
  } catch (err) {
    // Malformed URL — surface as a rejected promise, matching what
    // the native fetch would do for the same input. Reject with the
    // original error so callers can `instanceof TypeError`-check as
    // they would with the platform fetch.
    return Promise.reject(err);
  }

  if (!isAllowed(target)) {
    return Promise.reject(new NetworkPolicyError(target.toString()));
  }
  return nativeFetch(input, init);
}) as FetchFn;

(authorizedFetch as MarkedFetch & { [WRAPPER_MARKER]: true })[WRAPPER_MARKER] = true;

/**
 * Installs the wrapper on `globalThis.fetch` AND shims
 * `navigator.sendBeacon` — both are outbound-data transports the
 * S-003 acceptance criterion names ("no fetch/xhr/beacon call after
 * page load"). Idempotent — repeat calls are a no-op once the marker
 * is present. Call from `main.tsx` before any React import so
 * third-party module-level fetches (if any ever appear) are already
 * gated.
 */
export function installAuthorizedFetch(): void {
  const current = globalThis.fetch as MarkedFetch;
  if (current[WRAPPER_MARKER] === true) {
    return;
  }
  globalThis.fetch = authorizedFetch;
  installBeaconShim();
}

// Shims `navigator.sendBeacon` so beacon transport is gated by the
// same allow-list as `fetch`. Returns `false` when the target origin
// is not allowed — matches the browser's own quota/policy denial
// contract (`false` = "beacon not queued"), so existing callers'
// error handling paths still fire. Also emits a `console.warn` so
// a developer chasing a silently-dropped beacon has a signal.
//
// In test environments where `navigator` (happy-dom) or
// `navigator.sendBeacon` (older shims) is missing, this is a no-op.
function installBeaconShim(): void {
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
    return;
  }
  const originalSendBeacon = navigator.sendBeacon.bind(navigator);
  navigator.sendBeacon = function gatedSendBeacon(
    url: string | URL,
    data?: BodyInit | null,
  ): boolean {
    let target: URL;
    try {
      target = extractUrl(url);
    } catch {
      return false;
    }
    if (!isAllowed(target)) {
      console.warn(
        `[authorizedFetch] blocked navigator.sendBeacon to ${redactSensitiveQuery(target.toString())}`,
      );
      return false;
    }
    return originalSendBeacon(url, data);
  };
}

// Extracts the absolute URL from a `fetch` input. Handles the three
// documented shapes (string, `URL`, `Request`) and resolves relatives
// against `location.href` so same-origin `fetch('/api/foo')` is
// checked against the current document's origin, not a hard-coded
// fallback. In non-browser envs (Node test workers) `location` may
// be absent — `about:blank` gives us a deterministic, blocked-anyway
// origin.
function extractUrl(input: RequestInfo | URL): URL {
  if (typeof input === 'string') {
    return new URL(input, locationHref());
  }
  if (input instanceof URL) {
    return input;
  }
  return new URL(input.url);
}

function locationHref(): string {
  if (typeof globalThis.location !== 'undefined' && globalThis.location.href) {
    return globalThis.location.href;
  }
  return 'about:blank';
}
